import type { ReactNode } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "../utils/cn";

export interface NavBarItem {
  /** Matches the `value` of the corresponding <TabsContent>. */
  value: string;
  label: ReactNode;
}

export interface NavBarProps {
  items: NavBarItem[];
  className?: string;
  /**
   * Visual tone. `banner` (default) is the dark strip under the app banner;
   * `panel` is a lighter, primary-accented variant for nested / inner-level
   * tab strips (e.g. sub-tabs inside a workspace).
   */
  tone?: "banner" | "panel";
}

const TONES = {
  banner: {
    list: "border-sidebar-hover bg-sidebar text-sidebar-foreground",
    trigger:
      "text-sidebar-foreground/70 hover:bg-sidebar-hover hover:text-sidebar-foreground data-[state=active]:border-sidebar-active data-[state=active]:text-sidebar-foreground",
  },
  panel: {
    list: "border-border bg-panel text-panel-foreground",
    trigger:
      "text-panel-foreground/60 hover:text-panel-foreground data-[state=active]:border-primary data-[state=active]:text-panel-foreground",
  },
} as const;

/**
 * Branded navigation bar — a horizontal strip of underline-active items. The
 * default `banner` tone sits directly under the app banner; the `panel` tone is
 * for nested inner-level tab strips. It switches tab surfaces, so render it
 * inside a <Tabs> root (from this kit) alongside the matching <TabsContent>:
 *
 *   <Tabs defaultValue="home">
 *     <AppShell header={<><Banner/><NavBar items={[...]} /></>}>
 *       <TabsContent value="home">…</TabsContent>
 *       <TabsContent value="files">…</TabsContent>
 *     </AppShell>
 *   </Tabs>
 */
export function NavBar({ items, className, tone = "banner" }: NavBarProps) {
  const t = TONES[tone];
  return (
    <TabsPrimitive.List
      className={cn(
        "flex h-auto w-full items-center justify-start gap-1 border-b px-4",
        t.list,
        className,
      )}
    >
      {items.map((item) => (
        <TabsPrimitive.Trigger
          key={item.value}
          value={item.value}
          className={cn(
            "border-b-2 border-transparent px-4 py-3 text-sm font-medium transition-colors",
            "focus-visible:ring-primary focus-visible:outline-none focus-visible:ring-2",
            t.trigger,
          )}
        >
          {item.label}
        </TabsPrimitive.Trigger>
      ))}
    </TabsPrimitive.List>
  );
}
NavBar.displayName = "NavBar";
